/**
 * Fase 1 Blokk 5 T1.17 — Admin kandidat-status-matrise for kurs med sammensatt vurdering
 *
 * Rader = kandidater, kolonner = komponenter, celler = submission-status.
 * Vises kun for kurs med kompositt-plan (composition_mode != 'single_component').
 * Kalles fra CourseInstanceDetail → «Sensur-oversikt»-tab.
 */
import { useEffect, useState } from 'react';
import { CheckCircle, Clock, Circle, XCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Component {
  id: string;
  component_type: string;
  title: string;
  sequence: number;
  weight_pct: number | null;
  is_required: boolean;
  deadline: string | null;
}

interface Submission {
  status: string;
  submitted_at: string | null;
  sensor_score: number | null;
  sensor_grade: string | null;
  sensor_passed: boolean | null;
  sensor_graded_at: string | null;
  alternative_assessment_used: boolean | null;
  ai_preview_status: string | null;
}

interface Candidate {
  id: string;
  candidate_code: string | null;
  full_name: string | null;
  email: string | null;
  candidate_status: string;
  final_status: string | null;
  overall_grade: string | null;
  overall_passed: boolean | null;
  calculated_score: number | null;
  submissions: Record<string, Submission>;
}

interface MatrixResponse {
  has_plan: boolean;
  is_composite: boolean;
  plan_model: string | null;
  components: Component[];
  candidates: Candidate[];
}

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  written: 'Skriftlig',
  project: 'Prosjekt',
  oral: 'Muntlig',
  practical: 'Praktisk',
  portfolio: 'Mappe',
  observation: 'Observasjon',
  reflection: 'Refleksjon',
  group: 'Gruppe'
};

function StatusCell({ sub }: { sub: Submission | undefined }) {
  if (!sub) {
    return (
      <div className="flex items-center gap-1 text-xs text-gray-400" title="Ikke påbegynt">
        <Circle className="h-3 w-3" />
        <span>–</span>
      </div>
    );
  }
  const status = sub.status;
  if (status === 'graded') {
    return (
      <div className="flex items-center gap-1 text-xs text-emerald-700" title="Sensurert">
        <CheckCircle className="h-3 w-3" />
        <span>{sub.sensor_grade || sub.sensor_score || '✓'}</span>
      </div>
    );
  }
  if (status === 'overridden_by_alternative') {
    return (
      <div className="flex items-center gap-1 text-xs text-purple-700" title="Alternativ vurdering">
        <Sparkles className="h-3 w-3" />
        <span>Alt.</span>
      </div>
    );
  }
  if (status === 'submitted') {
    return (
      <div className="flex items-center gap-1 text-xs text-kf-blue-700" title="Levert, venter på sensur">
        <Clock className="h-3 w-3" />
        <span>Levert</span>
      </div>
    );
  }
  if (status === 'in_progress') {
    return (
      <div className="flex items-center gap-1 text-xs text-kf-orange-700" title="Under arbeid">
        <Circle className="h-3 w-3" />
        <span>Underveis</span>
      </div>
    );
  }
  if (status === 'failed' || status === 'expired') {
    return (
      <div className="flex items-center gap-1 text-xs text-red-700" title={status}>
        <XCircle className="h-3 w-3" />
        <span>{status}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500">
      <Circle className="h-3 w-3" />
      <span>{status}</span>
    </div>
  );
}

function finalBadge(candidate: Candidate) {
  if (!candidate.final_status) return null;
  const s = candidate.final_status;
  if (s === 'finalized') {
    const color = candidate.overall_passed ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800';
    return <span className={`text-xs px-2 py-0.5 rounded ${color}`}>Fullført · {candidate.overall_grade || '—'}</span>;
  }
  if (s === 'ready_to_finalize') {
    return <span className="text-xs px-2 py-0.5 rounded bg-kf-blue-50 text-kf-blue-800">Klar til finalisering</span>;
  }
  if (s === 'awaiting_grading') {
    return <span className="text-xs px-2 py-0.5 rounded bg-kf-orange-50 text-kf-orange-800">Avventer sensur</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{s.replace(/_/g, ' ')}</span>;
}

interface Props {
  courseInstanceId: string;
  onFinalize?: (candidateId: string) => void;
}

export default function CompositeAssessmentMatrix({ courseInstanceId, onFinalize }: Props) {
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMatrix = () => {
    setLoading(true);
    setError(null);
    authenticatedFetch(`/api/admin/assessment-plans/${courseInstanceId}/matrix`)
      .then(r => {
        if (!r.ok) {
          // Skill transiente 502/503 (backend midlertidig nede, f.eks. restart)
          // fra reelle feil slik at vi kan gi tydelig retry-oppfordring.
          if (r.status === 502 || r.status === 503 || r.status === 504) {
            const err: Error & { transient?: boolean } = new Error(
              'Vurderings-tjenesten er midlertidig utilgjengelig (sannsynligvis en pågående oppdatering). Prøv igjen om et øyeblikk.'
            );
            err.transient = true;
            throw err;
          }
          throw new Error(`HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(json => { setData(json); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { loadMatrix(); }, [courseInstanceId]);

  if (loading) return <div className="p-6 text-gray-600">Laster vurderings-matrise…</div>;
  if (error) {
    const isTransient = /midlertidig utilgjengelig/.test(error);
    return (
      <div className={`${isTransient ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-800'} border rounded-lg p-4`}>
        <div className="flex items-center gap-2 font-medium mb-1">
          <AlertTriangle className="h-4 w-4" />
          {isTransient ? 'Prøv igjen om et øyeblikk' : 'Kunne ikke hente matrise'}
        </div>
        <div className="text-sm">{error}</div>
        <button
          onClick={loadMatrix}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white border border-current rounded hover:bg-opacity-80"
        >
          Prøv igjen
        </button>
      </div>
    );
  }
  if (!data || !data.has_plan) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-gray-600">
        Ingen vurderings-oppskrift er registrert for dette kurset.
      </div>
    );
  }
  if (!data.is_composite) {
    return (
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 text-kf-blue-900">
        <div className="font-medium mb-1">Enkelt-komponent-vurdering</div>
        <div className="text-sm">
          Dette kurset har kun én vurderings-komponent (skriftlig prøve). Matrise-visning brukes for kurs med
          flere komponenter (kompositt-vurdering). Se «Prøve»-fanen for status.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm text-gray-700">
          <span className="font-medium">Modell:</span> {data.plan_model === 'weighted_composite' ? 'Vektet sammensatt (weights)' : 'Alle komponenter må bestås'}
          <span className="mx-3">·</span>
          <span className="font-medium">{data.candidates.length}</span> kandidater ·
          <span className="font-medium ml-1">{data.components.length}</span> komponenter
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">Kandidat</th>
              {data.components.map(c => (
                <th key={c.id} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  <div className="flex flex-col">
                    <span>{c.title || COMPONENT_TYPE_LABELS[c.component_type] || c.component_type}</span>
                    <span className="text-[10px] text-gray-400 font-normal">
                      {COMPONENT_TYPE_LABELS[c.component_type] || c.component_type}
                      {c.weight_pct != null && ` · ${c.weight_pct}%`}
                      {!c.is_required && ' · valgfri'}
                    </span>
                  </div>
                </th>
              ))}
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Helhet</th>
              <th className="px-3 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.candidates.length === 0 && (
              <tr>
                <td colSpan={data.components.length + 3} className="px-4 py-6 text-center text-gray-500 text-sm">
                  Ingen kandidater er registrert på kurset ennå.
                </td>
              </tr>
            )}
            {data.candidates.map(cand => (
              <tr key={cand.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm sticky left-0 bg-white hover:bg-gray-50">
                  <div className="font-medium text-gray-900">{cand.candidate_code || cand.full_name || '(uten kode)'}</div>
                  {cand.full_name && cand.candidate_code && (
                    <div className="text-xs text-gray-500">{cand.full_name}</div>
                  )}
                </td>
                {data.components.map(comp => (
                  <td key={comp.id} className="px-3 py-3">
                    <StatusCell sub={cand.submissions[comp.id]} />
                  </td>
                ))}
                <td className="px-3 py-3">
                  {finalBadge(cand)}
                  {cand.calculated_score != null && cand.final_status !== 'finalized' && (
                    <div className="text-xs text-gray-500 mt-1">Foreløpig: {Number(cand.calculated_score).toFixed(0)}%</div>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  {onFinalize && cand.final_status !== 'finalized' && (
                    <button
                      onClick={() => onFinalize(cand.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                    >
                      Finaliser
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-gray-500 flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> Sensurert</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-kf-blue-600" /> Levert, venter på sensur</span>
        <span className="inline-flex items-center gap-1"><Circle className="h-3 w-3 text-kf-orange-500" /> Under arbeid</span>
        <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-purple-600" /> Alternativ vurdering</span>
        <span className="inline-flex items-center gap-1"><Circle className="h-3 w-3 text-gray-400" /> Ikke påbegynt</span>
      </div>
    </div>
  );
}
